import { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Segmented, Select, Button, Modal, Form, DatePicker, message } from "antd";
import { SearchOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useProjects, useCreateProject, useUpdateProject, ProjectInput } from "../../queries/projects";
import { useStaffDirectory, StaffMember } from "../../queries/users";
import { ProjectProps, ProjectStatus, Severity, Priority } from "../../types/interfaces";
import { ApiError } from "../../hooks/apiRequest";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { PageShell, Toolbar } from "../../components/ui/PageShell";
import { ProjectStatusTag, SeverityChip, PriorityChip } from "../../components/ui/StatusTag";
import { EmptyState } from "../../components/ui/States";
import { SEVERITY_OPTIONS, PRIORITY_OPTIONS } from "../../utils/levels";
import AuthContext from "../../context/AuthContext";
import styles from "./projects.module.css";

const ORDER: ProjectStatus[] = ["active", "on_hold", "completed", "archived"];
const GROUP_LABEL: Record<ProjectStatus, string> = {
  active: "Active", on_hold: "On hold", completed: "Completed", archived: "Archived",
};
const STATUS_OPTIONS = ORDER.map((s) => ({ value: s, label: GROUP_LABEL[s] }));

interface ProjectFormValues {
  name: string;
  description?: string;
  status?: ProjectStatus;
  severity: Severity;
  priority: Priority;
  leadId?: string | null;
  assignedUserIds?: string[];
  dates?: [dayjs.Dayjs, dayjs.Dayjs];
}

/**
 * Projects is a card grid, not a table — deliberately. It's the one list you
 * browse and pick from rather than scan and sort. OSI is the only client, so
 * every project belongs to it; what matters is who is assigned and how
 * severe / urgent the work is.
 */
export const ProjectListPage = () => {
  const navigate = useNavigate();
  const { userData, isManager } = useContext(AuthContext);
  const isStaff = userData?.role === "manager" || userData?.role === "agent";
  const [severity, setSeverity] = useState<Severity | undefined>();
  const [priority, setPriority] = useState<Priority | undefined>();
  const [assigneeId, setAssigneeId] = useState<string | undefined>();
  const { data: projects, isLoading, isError, error } = useProjects({ severity, priority, assigneeId });
  const { data: staff } = useStaffDirectory({ enabled: isStaff });
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"open" | "all">("open");
  const [editing, setEditing] = useState<ProjectProps | "new" | null>(null);
  const [form] = Form.useForm<ProjectFormValues>();

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (projects || [])
      .filter((p: ProjectProps) => (filter === "open" ? p.status === "active" || p.status === "on_hold" : true))
      .filter((p: ProjectProps) => (q ? p.name.toLowerCase().includes(q) : true));
  }, [projects, query, filter]);

  const groups = ORDER.map((status) => ({ status, items: rows.filter((p: ProjectProps) => p.status === status) }))
    .filter((g) => g.items.length > 0);

  const staffName = (id: string | null) => (id ? (staff || []).find((u: StaffMember) => u._id === id)?.name : undefined);

  const openForm = (project: ProjectProps | "new") => {
    form.resetFields();
    if (project !== "new") {
      form.setFieldsValue({
        name: project.name,
        description: project.description,
        status: project.status,
        severity: project.severity,
        priority: project.priority,
        leadId: project.leadId,
        assignedUserIds: project.assignedUserIds,
        dates: project.startDate && project.targetDate ? [dayjs(project.startDate), dayjs(project.targetDate)] : undefined,
      });
    }
    setEditing(project);
  };

  const handleSave = async () => {
    const v = await form.validateFields();
    const data: ProjectInput & { name: string } = {
      name: v.name,
      description: v.description,
      severity: v.severity,
      priority: v.priority,
      ...(v.status ? { status: v.status } : {}),
      leadId: v.leadId || null,
      assignedUserIds: v.assignedUserIds || [],
      ...(v.dates ? { startDate: v.dates[0].toISOString(), targetDate: v.dates[1].toISOString() } : {}),
    };
    try {
      if (editing && editing !== "new") await updateProject.mutateAsync({ id: editing._id, data });
      else await createProject.mutateAsync(data);
      setEditing(null);
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not save the project");
    }
  };

  return (
    <PageShell
      kind="list"
      kicker="Delivery"
      title="Projects"
      subtitle="Everything we're supporting, grouped by where it stands — with who is on it and how severe it is."
      actions={isManager && <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm("new")}>New project</Button>}
    >
      <Toolbar count={rows.length} unit="projects">
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: "var(--osi-text-faint)" }} />}
          placeholder="Search projects"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: 240 }}
        />
        <Select allowClear style={{ width: 150 }} placeholder="Any severity" value={severity} onChange={setSeverity} options={SEVERITY_OPTIONS} />
        <Select allowClear style={{ width: 150 }} placeholder="Any priority" value={priority} onChange={setPriority} options={PRIORITY_OPTIONS} />
        {isStaff && (
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 170 }}
            placeholder="Any assignee"
            value={assigneeId}
            onChange={setAssigneeId}
            options={(staff || []).map((u: StaffMember) => ({ label: u.name, value: u._id }))}
          />
        )}
        <Segmented
          value={filter}
          onChange={(v) => setFilter(v as "open" | "all")}
          options={[{ label: "Open", value: "open" }, { label: "All", value: "all" }]}
        />
      </Toolbar>

      <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
        {groups.length === 0 ? (
          <EmptyState
            title="No projects match"
            description="Clear the filters, or switch to All to include completed and archived work."
          />
        ) : (
          groups.map(({ status, items }) => (
            <section key={status}>
              <div className={styles.groupHead}>
                <span className={styles.groupTitle}>{GROUP_LABEL[status]}</span>
                <span className={styles.groupCount}>{items.length}</span>
              </div>
              <div className={styles.grid}>
                {items.map((p: ProjectProps) => (
                  <div key={p._id} className={styles.card}>
                    <button
                      type="button"
                      className={styles.cardLink}
                      onClick={() => navigate(`/projects/${p._id}/board`)}
                    >
                      {p.name}
                    </button>
                    <p className={styles.desc}>{p.description || "No description yet."}</p>
                    <span className={styles.tags}>
                      <SeverityChip severity={p.severity} />
                      <PriorityChip priority={p.priority} />
                    </span>
                    <span className={styles.foot}>
                      <ProjectStatusTag status={p.status} />
                      <span className={styles.client}>
                        {staffName(p.leadId) ? `Lead: ${staffName(p.leadId)} · ` : ""}
                        <span className="osi-num">{p.assignedUserIds.length}</span> assigned
                      </span>
                      {isManager && (
                        <Button size="small" className={styles.edit} onClick={() => openForm(p)} aria-label={`Edit ${p.name}`}>
                          Edit
                        </Button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          ))
        )}
      </DisplayComponent>

      <Modal
        title={editing && editing !== "new" ? "Edit project" : "New project"}
        open={editing !== null}
        onCancel={() => setEditing(null)}
        onOk={handleSave}
        okText={editing && editing !== "new" ? "Save" : "Create project"}
        confirmLoading={createProject.isPending || updateProject.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ severity: "minor", priority: "medium" }}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Form.Item name="severity" label="Severity" rules={[{ required: true }]}><Select options={SEVERITY_OPTIONS} /></Form.Item>
            <Form.Item name="priority" label="Priority" rules={[{ required: true }]}><Select options={PRIORITY_OPTIONS} /></Form.Item>
          </div>
          {editing && editing !== "new" && (
            <Form.Item name="status" label="Status"><Select options={STATUS_OPTIONS} /></Form.Item>
          )}
          <Form.Item name="leadId" label="Project lead">
            <Select allowClear showSearch optionFilterProp="label" placeholder="Who owns this project?"
              options={(staff || []).map((u: StaffMember) => ({ label: u.name, value: u._id }))} />
          </Form.Item>
          <Form.Item name="assignedUserIds" label="Assigned to">
            <Select mode="multiple" showSearch optionFilterProp="label" placeholder="Who works on it?"
              options={(staff || []).map((u: StaffMember) => ({ label: u.name, value: u._id }))} />
          </Form.Item>
          <Form.Item name="dates" label="Start → target date"><DatePicker.RangePicker style={{ width: "100%" }} /></Form.Item>
        </Form>
      </Modal>
    </PageShell>
  );
};
